// utils/documentExtractor.js
import { pdfjsLib } from './pdfjs-config';
import * as mammoth from 'mammoth';
import JSZip from 'jszip';
import { createWorker } from 'tesseract.js';

// Error codes as a plain object
export const ErrorCode = {
  UNSUPPORTED_FORMAT: 'UNSUPPORTED_FORMAT',
  FILE_TOO_LARGE: 'FILE_TOO_LARGE',
  CORRUPTED_FILE: 'CORRUPTED_FILE',
  EXTRACTION_FAILED: 'EXTRACTION_FAILED',
  NO_TEXT_CONTENT: 'NO_TEXT_CONTENT',
  INVALID_PDF: 'INVALID_PDF',
  INVALID_DOCX: 'INVALID_DOCX',
  INVALID_PPTX: 'INVALID_PPTX',
  PROCESSING_TIMEOUT: 'PROCESSING_TIMEOUT',
  OCR_FAILED: 'OCR_FAILED',
};

// Custom error class
export class DocumentProcessingError extends Error {
  constructor(message, code, fileName, originalError) {
    super(message);
    this.name = 'DocumentProcessingError';
    this.code = code;
    this.fileName = fileName;
    this.originalError = originalError;
  }
}

// Helper functions for image analysis
function analyzeImageColors(data) {
  const uniqueColors = new Set();
  let hasTransparency = false;
  let isGrayscale = true;
  let lastR = -1,
    lastG = -1,
    lastB = -1;

  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    const a = data[i + 3];

    if (a < 255) {
      hasTransparency = true;
    }

    // Check if image is grayscale
    if (lastR !== -1 && lastG !== -1 && lastB !== -1) {
      if (r !== g || g !== b) {
        isGrayscale = false;
      }
    }

    uniqueColors.add(`${r},${g},${b}`);
    lastR = r;
    lastG = g;
    lastB = b;

    if (uniqueColors.size > 10000) break; // Prevent memory issues
  }

  return {
    uniqueColors: uniqueColors.size,
    hasTransparency,
    isGrayscale,
  };
}

function calculateBrightness(data) {
  let totalBrightness = 0;
  let pixelCount = 0;

  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    const a = data[i + 3];

    if (a > 0) {
      // Calculate perceived brightness using the formula: (0.299*R + 0.587*G + 0.114*B)
      const brightness = ((0.299 * r + 0.587 * g + 0.114 * b) / 255) * 100;
      totalBrightness += brightness;
      pixelCount++;
    }
  }

  return pixelCount > 0 ? totalBrightness / pixelCount : 0;
}

function calculateContrast(data) {
  const brightnessValues = [];

  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    const a = data[i + 3];

    if (a > 0) {
      const brightness = ((0.299 * r + 0.587 * g + 0.114 * b) / 255) * 100;
      brightnessValues.push(brightness);
    }
  }

  if (brightnessValues.length === 0) return 0;

  const mean =
    brightnessValues.reduce((a, b) => a + b, 0) / brightnessValues.length;
  const variance =
    brightnessValues.reduce((a, b) => a + Math.pow(b - mean, 2), 0) /
    brightnessValues.length;
  return Math.sqrt(variance);
}

function getDominantColors(data, count) {
  const colorMap = new Map();

  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    const a = data[i + 3];

    if (a > 0) {
      const key = `${r},${g},${b}`;
      colorMap.set(key, (colorMap.get(key) || 0) + 1);
    }
  }

  return Array.from(colorMap.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, count)
    .map(([color]) => {
      const [r, g, b] = color.split(',').map(Number);
      return { r, g, b };
    });
}

/**
 * Main function to extract content from various file types
 */
export async function extractDocumentContent(file, options = {}) {
  const startTime = performance.now();
  const warnings = [];

  try {
    // Validate file first
    await validateFile(file, options);

    const fileType = getFileType(file);
    const result = {
      fileName: file.name,
      fileSize: file.size,
      fileType,
      lastModified: new Date(file.lastModified).toISOString(),
      content: '',
      metadata: {},
      extractedAt: new Date().toISOString(),
      processingTime: 0,
      success: false,
      warnings,
    };

    let extractionResult;

    switch (fileType) {
      case 'pdf':
        extractionResult = await extractPDFContent(file);
        break;
      case 'docx':
        extractionResult = await extractDocxContent(file);
        break;
      case 'pptx':
        extractionResult = await extractPptxContent(file);
        break;
      case 'image':
        extractionResult = await extractImageContent(file);
        break;
      case 'txt':
        extractionResult = await extractTextContent(file);
        break;
      default:
        throw new DocumentProcessingError(
          `Unsupported file type: ${fileType}`,
          ErrorCode.UNSUPPORTED_FORMAT,
          file.name
        );
    }

    result.content = extractionResult.text;
    result.metadata = extractionResult.metadata;
    result.warnings = [...warnings, ...(extractionResult.warnings || [])];
    result.processingTime = performance.now() - startTime;
    result.success = true;

    // Additional validation
    if (
      options.requireText &&
      (!result.content || result.content.trim().length === 0)
    ) {
      throw new DocumentProcessingError(
        'No text content found in document',
        ErrorCode.NO_TEXT_CONTENT,
        file.name
      );
    }

    if (
      options.minContentLength &&
      result.content.length < options.minContentLength
    ) {
      result.warnings.push(
        `Content length (${result.content.length}) is below minimum threshold (${options.minContentLength})`
      );
    }

    return result;
  } catch (error) {
    const processingTime = performance.now() - startTime;

    if (error instanceof DocumentProcessingError) {
      throw error;
    }

    throw new DocumentProcessingError(
      `Failed to extract content: ${error instanceof Error ? error.message : String(error)}`,
      ErrorCode.EXTRACTION_FAILED,
      file.name,
      error instanceof Error ? error : undefined
    );
  }
}

/**
 * Determine file type with enhanced detection
 */
export function getFileType(file) {
  const mimeType = file.type.toLowerCase();
  const extension = (file.name.split('.').pop() || '').toLowerCase();

  // PDF detection
  if (mimeType === 'application/pdf' || extension === 'pdf') {
    return 'pdf';
  }

  // Word document detection
  if (
    mimeType ===
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    extension === 'docx'
  ) {
    return 'docx';
  }

  // PowerPoint detection
  if (
    mimeType ===
      'application/vnd.openxmlformats-officedocument.presentationml.presentation' ||
    extension === 'pptx'
  ) {
    return 'pptx';
  }

  // Image detection
  if (
    mimeType.startsWith('image/') ||
    ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'tiff', 'svg'].includes(
      extension
    )
  ) {
    return 'image';
  }

  // Text detection
  if (mimeType === 'text/plain' || extension === 'txt') {
    return 'txt';
  }

  return 'unknown';
}

/**
 * Enhanced PDF content extraction
 */
async function extractPDFContent(file) {
  const warnings = [];
  let arrayBuffer;

  try {
    arrayBuffer = await file.arrayBuffer();
  } catch (error) {
    throw new DocumentProcessingError(
      'Failed to read PDF file',
      ErrorCode.CORRUPTED_FILE,
      file.name,
      error instanceof Error ? error : undefined
    );
  }

  let pdf;

  try {
    // Add more detailed configuration for PDF loading
    const loadingTask = pdfjsLib.getDocument({
      data: arrayBuffer,
      verbosity: 1, // Increase verbosity for debugging
      disableFontFace: true,
      standardFontDataUrl: undefined,
      cMapUrl: 'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/cmaps/',
      cMapPacked: true,
    });

    // Add progress callback for debugging
    loadingTask.onProgress = (progressData) => {
      
    };

    pdf = await loadingTask.promise;
  } catch (error) {
    console.error('PDF Loading Error:', error);
    throw new DocumentProcessingError(
      `Invalid or corrupted PDF file: ${error instanceof Error ? error.message : String(error)}`,
      ErrorCode.INVALID_PDF,
      file.name,
      error instanceof Error ? error : undefined
    );
  }

  let fullText = '';
  const metadata = {
    numPages: pdf.numPages,
    title: '',
    author: '',
    subject: '',
    creator: '',
    producer: '',
    creationDate: '',
    modificationDate: '',
    keywords: '',
    pdfVersion: '',
  };

  // Extract metadata with better error handling
  try {
    const info = await pdf.getMetadata();
    if (info.info) {
      metadata.title = info.info.Title || '';
      metadata.author = info.info.Author || '';
      metadata.subject = info.info.Subject || '';
      metadata.creator = info.info.Creator || '';
      metadata.producer = info.info.Producer || '';
      metadata.keywords = info.info.Keywords || '';

      // Handle dates safely
      if (info.info.CreationDate) {
        try {
          metadata.creationDate = new Date(
            info.info.CreationDate
          ).toISOString();
        } catch {
          metadata.creationDate = String(info.info.CreationDate);
        }
      }

      if (info.info.ModDate) {
        try {
          metadata.modificationDate = new Date(info.info.ModDate).toISOString();
        } catch {
          metadata.modificationDate = String(info.info.ModDate);
        }
      }
    }

    if (info.metadata) {
      metadata.pdfVersion = info.metadata.get('pdf:PDFVersion') || '';
    }
  } catch (error) {
    warnings.push('Could not extract PDF metadata completely');
  }

  // Extract text from each page with timeout protection
  const pagePromises = [];

  for (let i = 1; i <= pdf.numPages; i++) {
    pagePromises.push(
      Promise.race([
        extractPageText(pdf, i),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Page extraction timeout')), 30000)
        ),
      ]).catch((error) => {
        warnings.push(
          `Could not extract text from page ${i}: ${error.message}`
        );
        return `[Page ${i} - extraction failed]`;
      })
    );
  }

  try {
    const pageTexts = await Promise.all(pagePromises);
    fullText = pageTexts
      .map((text, index) => `\n--- Page ${index + 1} ---\n${text}`)
      .join('\n');
  } catch (error) {
    throw new DocumentProcessingError(
      'Failed to extract text from PDF pages',
      ErrorCode.EXTRACTION_FAILED,
      file.name,
      error instanceof Error ? error : undefined
    );
  }

  return {
    text: fullText.trim(),
    metadata,
    warnings,
  };
}

async function extractPageText(pdf, pageNumber) {
  try {
    const page = await pdf.getPage(pageNumber);
    const textContent = await page.getTextContent();

    // Enhanced text extraction with better spacing
    let pageText = '';
    let lastY = 0;

    for (const item of textContent.items) {
      if ('str' in item && 'transform' in item) {
        const currentY = item.transform[5];

        // Add line break if we've moved to a different line
        if (lastY !== 0 && Math.abs(currentY - lastY) > 5) {
          pageText += '\n';
        }

        pageText += item.str + ' ';
        lastY = currentY;
      }
    }

    return pageText.trim();
  } catch (error) {
    throw new Error(
      `Failed to extract page ${pageNumber}: ${error instanceof Error ? error.message : String(error)}`
    );
  }
}

/**
 * Enhanced DOCX content extraction
 */
async function extractDocxContent(file) {
  const warnings = [];
  let arrayBuffer;

  try {
    arrayBuffer = await file.arrayBuffer();
  } catch (error) {
    throw new DocumentProcessingError(
      'Failed to read DOCX file',
      ErrorCode.CORRUPTED_FILE,
      file.name,
      error instanceof Error ? error : undefined
    );
  }

  let result;

  try {
    result = await mammoth.extractRawText({
      arrayBuffer,
    });
  } catch (error) {
    throw new DocumentProcessingError(
      'Invalid or corrupted DOCX file',
      ErrorCode.INVALID_DOCX,
      file.name,
      error instanceof Error ? error : undefined
    );
  }

  // Enhanced metadata extraction
  const text = result.value;
  const lines = text.split('\n');
  const words = text.split(/\s+/).filter((word) => word.length > 0);

  const metadata = {
    hasImages: false,
    wordCount: words.length,
    characterCount: text.length,
    paragraphCount: lines.filter((line) => line.trim().length > 0).length,
    hasHeaders: false,
    hasFooters: false,
  };

  // Check for structural elements
  try {
    const zip = new JSZip();
    const zipContent = await zip.loadAsync(arrayBuffer);

    // Check for images
    const mediaFiles = Object.keys(zipContent.files).filter(
      (name) =>
        name.startsWith('word/media/') &&
        /\.(jpg|jpeg|png|gif|bmp|svg)$/i.test(name)
    );
    metadata.hasImages = mediaFiles.length > 0;

    // Check for headers and footers
    const headerFiles = Object.keys(zipContent.files).filter(
      (name) => name.startsWith('word/header') && name.endsWith('.xml')
    );
    const footerFiles = Object.keys(zipContent.files).filter(
      (name) => name.startsWith('word/footer') && name.endsWith('.xml')
    );

    metadata.hasHeaders = headerFiles.length > 0;
    metadata.hasFooters = footerFiles.length > 0;
  } catch (error) {
    warnings.push('Could not extract detailed document structure information');
  }

  // Add warnings from mammoth
  if (result.messages && result.messages.length > 0) {
    warnings.push(...result.messages.map((msg) => `Mammoth: ${msg.message}`));
  }

  return {
    text: text,
    metadata,
    warnings,
  };
}

/**
 * Enhanced PPTX content extraction
 */
async function extractPptxContent(file) {
  const warnings = [];
  let arrayBuffer;

  try {
    arrayBuffer = await file.arrayBuffer();
  } catch (error) {
    throw new DocumentProcessingError(
      'Failed to read PPTX file',
      ErrorCode.CORRUPTED_FILE,
      file.name,
      error instanceof Error ? error : undefined
    );
  }

  let zip;

  try {
    zip = new JSZip();
    await zip.loadAsync(arrayBuffer);
  } catch (error) {
    throw new DocumentProcessingError(
      'Invalid or corrupted PPTX file',
      ErrorCode.INVALID_PPTX,
      file.name,
      error instanceof Error ? error : undefined
    );
  }

  let fullText = '';
  let slideCount = 0;
  const slides = [];

  const metadata = {
    totalSlides: 0,
    slides: [],
    hasImages: false,
    hasTables: false,
  };

  // Get slide files in correct order
  const slideFiles = Object.keys(zip.files)
    .filter(
      (name) => name.startsWith('ppt/slides/slide') && name.endsWith('.xml')
    )
    .sort((a, b) => {
      const numA = parseInt((a.match(/slide(\d+)\.xml$/) || [])[1] || '0');
      const numB = parseInt((b.match(/slide(\d+)\.xml$/) || [])[1] || '0');
      return numA - numB;
    });

  // Check for media files
  const mediaFiles = Object.keys(zip.files).filter(
    (name) =>
      name.startsWith('ppt/media/') &&
      /\.(jpg|jpeg|png|gif|bmp|svg)$/i.test(name)
  );
  metadata.hasImages = mediaFiles.length > 0;

  for (const slideFile of slideFiles) {
    try {
      const slideXml = await zip.files[slideFile].async('text');
      const slideResult = await extractSlideContent(slideXml, slideCount + 1);

      if (slideResult.text.trim()) {
        slideCount++;
        fullText += `\n--- Slide ${slideCount} ---\n${slideResult.text}\n`;
        slides.push(slideResult.metadata);

        if (slideResult.metadata.hasTables) {
          metadata.hasTables = true;
        }
      }
    } catch (error) {
      warnings.push(
        `Could not extract text from slide ${slideCount + 1}: ${error instanceof Error ? error.message : String(error)}`
      );
    }
  }

  metadata.totalSlides = slideCount;
  metadata.slides = slides;

  return {
    text: fullText.trim(),
    metadata,
    warnings,
  };
}

async function extractSlideContent(slideXml, slideNumber) {
  // Enhanced XML text extraction
  const textRegex = /<a:t[^>]*>([^<]*)<\/a:t>/g;
  const titleRegex = /<p:ph [^>]*type="title"[^>]*>[\s\S]*?<\/p:sp>/;

  const texts = [];
  let match;

  while ((match = textRegex.exec(slideXml)) !== null) {
    if (match[1] && match[1].trim()) {
      texts.push(match[1].trim());
    }
  }

  const slideText = texts.join(' ');

  // Extract title if present
  const titleMatch = slideXml.match(titleRegex);
  let title = '';
  if (titleMatch) {
    const titleTextMatch = titleMatch[0].match(/<a:t[^>]*>([^<]*)<\/a:t>/);
    title = (titleTextMatch && titleTextMatch[1]) || '';
  }

  const metadata = {
    slideNumber,
    textLength: slideText.length,
    hasImages: slideXml.includes('<a:blip') || slideXml.includes('<pic:pic'),
    hasTables: slideXml.includes('<a:tbl') || slideXml.includes('<a:gridCol'),
    title: title || undefined,
  };

  return {
    text: slideText,
    metadata,
  };
}

/**
 * Enhanced image content extraction
 */
async function extractImageContent(file) {
  const warnings = [];
  const metadata = {
    width: 0,
    height: 0,
    type: file.type,
    ocrAvailable: true,
    hasText: false,
  };

  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    // Set a timeout for image loading
    const timeout = setTimeout(() => {
      URL.revokeObjectURL(url);
      reject(
        new DocumentProcessingError(
          'Image loading timed out',
          ErrorCode.PROCESSING_TIMEOUT,
          file.name
        )
      );
    }, 30000); // 30 second timeout

    img.onload = async () => {
      clearTimeout(timeout);
      try {
        metadata.width = img.width;
        metadata.height = img.height;
        URL.revokeObjectURL(url);

        // Create canvas for image analysis
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          throw new Error('Could not get canvas context');
        }

        // Set canvas size to match image
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);

        // Get image data for analysis
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;

        // Analyze image properties
        const colorStats = analyzeImageColors(data);
        const brightness = calculateBrightness(data);
        const contrast = calculateContrast(data);
        const dominantColors = getDominantColors(data, 5);

        // Perform OCR on the image
        let extractedText = '';
        let confidence = 0;

        try {
          // Initialize Tesseract worker with minimal configuration
          const worker = await createWorker('eng');

          // Enhance image for better OCR
          ctx.filter = 'contrast(1.2) brightness(1.1)';
          ctx.drawImage(img, 0, 0);

          // Perform OCR
          const { data: ocrResult } = await worker.recognize(canvas);

          extractedText = ocrResult.text;
          confidence = ocrResult.confidence;
          metadata.hasText = extractedText.trim().length > 0;

          // Clean up worker
          await worker.terminate();
        } catch (ocrError) {
          warnings.push(
            `OCR processing failed: ${ocrError instanceof Error ? ocrError.message : String(ocrError)}`
          );
        }

        // Create detailed image analysis
        const imageInfo = {
          name: file.name,
          dimensions: `${img.width}×${img.height} pixels`,
          aspectRatio: (img.width / img.height).toFixed(2),
          type: file.type,
          size: `${(file.size / 1024).toFixed(2)} KB`,
          colorDepth: `${colorStats.uniqueColors} unique colors`,
          brightness: `${brightness.toFixed(2)}%`,
          contrast: `${contrast.toFixed(2)}%`,
          dominantColors: dominantColors.map(
            (color) => `rgb(${color.r}, ${color.g}, ${color.b})`
          ),
          hasTransparency: colorStats.hasTransparency,
          isGrayscale: colorStats.isGrayscale,
          extractedText: extractedText,
          confidence: confidence,
        };

        // Generate detailed text analysis
        const text = `[Image Analysis]
Name: ${imageInfo.name}
Dimensions: ${imageInfo.dimensions}
Aspect Ratio: ${imageInfo.aspectRatio}
Type: ${imageInfo.type}
Size: ${imageInfo.size}
Color Analysis:
- Unique Colors: ${imageInfo.colorDepth}
- Brightness: ${imageInfo.brightness}
- Contrast: ${imageInfo.contrast}
- Dominant Colors: ${imageInfo.dominantColors.join(', ')}
- Has Transparency: ${imageInfo.hasTransparency}
- Is Grayscale: ${imageInfo.isGrayscale}

${
  extractedText
    ? `Extracted Text (Confidence: ${confidence.toFixed(2)}%):
${extractedText}`
    : 'No text could be extracted from the image.'
}`;

        resolve({
          text,
          metadata: {
            ...metadata,
            colorDepth: colorStats.uniqueColors,
            dominantColors: imageInfo.dominantColors,
            brightness,
            contrast,
            hasTransparency: colorStats.hasTransparency,
            isGrayscale: colorStats.isGrayscale,
            extractedText: extractedText,
            confidence: confidence,
          },
          warnings,
        });
      } catch (error) {
        URL.revokeObjectURL(url);
        reject(
          new DocumentProcessingError(
            'Failed to process image',
            ErrorCode.EXTRACTION_FAILED,
            file.name,
            error instanceof Error ? error : undefined
          )
        );
      }
    };

    img.onerror = () => {
      clearTimeout(timeout);
      URL.revokeObjectURL(url);
      reject(
        new DocumentProcessingError(
          'Failed to load image',
          ErrorCode.CORRUPTED_FILE,
          file.name
        )
      );
    };

    img.src = url;
  });
}

/**
 * Enhanced text content extraction
 */
async function extractTextContent(file) {
  const warnings = [];
  let text;

  try {
    text = await file.text();
  } catch (error) {
    throw new DocumentProcessingError(
      'Failed to read text file',
      ErrorCode.CORRUPTED_FILE,
      file.name,
      error instanceof Error ? error : undefined
    );
  }

  const lines = text.split(/\r?\n/);
  const words = text.split(/\s+/).filter((word) => word.length > 0);

  // Detect encoding (basic heuristic)
  let encoding = 'UTF-8';
  if (text.includes('\uFFFD')) {
    encoding = 'Unknown (possibly corrupted)';
    warnings.push('Text file may have encoding issues');
  }

  const metadata = {
    lineCount: lines.length,
    wordCount: words.length,
    characterCount: text.length,
    encoding,
  };

  return {
    text,
    metadata,
    warnings,
  };
}

/**
 * Enhanced file validation
 */
export async function validateFile(file, options = {}) {
  const {
    maxSize = 50 * 1024 * 1024, // 50MB default
    allowedTypes = ['pdf', 'docx', 'pptx', 'txt'],
  } = options;

  if (!file) {
    throw new DocumentProcessingError(
      'No file provided',
      ErrorCode.EXTRACTION_FAILED
    );
  }

  if (file.size === 0) {
    throw new DocumentProcessingError(
      'File is empty',
      ErrorCode.CORRUPTED_FILE,
      file.name
    );
  }

  if (file.size > maxSize) {
    throw new DocumentProcessingError(
      `File size (${(file.size / 1024 / 1024).toFixed(2)}MB) exceeds maximum allowed size (${(maxSize / 1024 / 1024).toFixed(2)}MB)`,
      ErrorCode.FILE_TOO_LARGE,
      file.name
    );
  }

  const fileType = getFileType(file);
  if (!allowedTypes.includes(fileType)) {
    throw new DocumentProcessingError(
      `File type '${fileType}' is not supported. Allowed types: ${allowedTypes.join(', ')}`,
      ErrorCode.UNSUPPORTED_FORMAT,
      file.name
    );
  }

  // Additional file integrity checks
  if (fileType === 'pdf') {
    await validatePDFIntegrity(file);
  } else if (fileType === 'docx' || fileType === 'pptx') {
    await validateOfficeDocumentIntegrity(file);
  }
}

async function validatePDFIntegrity(file) {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const header = new Uint8Array(arrayBuffer.slice(0, 5));
    const headerString = Array.from(header)
      .map((byte) => String.fromCharCode(byte))
      .join('');

    if (!headerString.startsWith('%PDF')) {
      throw new DocumentProcessingError(
        'Invalid PDF file header',
        ErrorCode.INVALID_PDF,
        file.name
      );
    }
  } catch (error) {
    if (error instanceof DocumentProcessingError) {
      throw error;
    }
    throw new DocumentProcessingError(
      'Could not validate PDF file integrity',
      ErrorCode.CORRUPTED_FILE,
      file.name,
      error instanceof Error ? error : undefined
    );
  }
}

async function validateOfficeDocumentIntegrity(file) {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const zip = new JSZip();
    await zip.loadAsync(arrayBuffer);

    // Check for required Office document structure
    const hasContentTypes = '[Content_Types].xml' in zip.files;
    const hasRels = '_rels/.rels' in zip.files;

    if (!hasContentTypes || !hasRels) {
      throw new DocumentProcessingError(
        'Invalid Office document structure',
        file.name.endsWith('.docx')
          ? ErrorCode.INVALID_DOCX
          : ErrorCode.INVALID_PPTX,
        file.name
      );
    }
  } catch (error) {
    if (error instanceof DocumentProcessingError) {
      throw error;
    }
    throw new DocumentProcessingError(
      'Could not validate Office document integrity',
      ErrorCode.CORRUPTED_FILE,
      file.name,
      error instanceof Error ? error : undefined
    );
  }
}

/**
 * Enhanced batch processing with better error handling and progress tracking
 */
export async function batchExtractContent(files, options = {}) {
  const startTime = performance.now();
  const results = [];
  const errors = [];

  const fileArray = Array.from(files);

  // Process files concurrently but with limited concurrency
  const batchSize = 3; // Process 3 files at a time
  const batches = [];

  for (let i = 0; i < fileArray.length; i += batchSize) {
    batches.push(fileArray.slice(i, i + batchSize));
  }

  for (const batch of batches) {
    const batchPromises = batch.map(async (file) => {
      try {
        const result = await extractDocumentContent(file, options);
        results.push(result);
      } catch (error) {
        const processingError = {
          fileName: file.name,
          fileSize: file.size,
          fileType: getFileType(file),
          error: error instanceof Error ? error.message : String(error),
          errorCode:
            error instanceof DocumentProcessingError
              ? error.code
              : ErrorCode.EXTRACTION_FAILED,
          timestamp: new Date().toISOString(),
        };
        errors.push(processingError);
      }
    });

    await Promise.allSettled(batchPromises);
  }

  const totalProcessingTime = performance.now() - startTime;

  return {
    results,
    errors,
    processed: results.length,
    failed: errors.length,
    totalProcessingTime,
  };
}
